import React, { useState, useEffect, useRef, useCallback } from "react";
import ModelViewer from './ModelViewer';
import { getQualityBorderColor } from '../lib/itemQuality';
import "./CharacterPanel.css";
import itemsData from '../items.json';
// Equipment sprite for empty slot placeholders
const EQUIP_SPRITE = require('../assets/equipment-overview.png');
const ALLIANCE_ICON = require('../assets/alliance.png');
const HORDE_ICON = require('../assets/horde.png');

// Build a quick lookup map from items.json at module load time: itemID -> GearScore
const GEAR_SCORE_MAP = (() => {
  try {
    const m = {};
    for (const it of itemsData) {
      if (it && typeof it.itemID !== 'undefined') m[it.itemID] = it.GearScore || 0;
    }
    return m;
  } catch (_e) {
    return {};
  }
})();

// Build a quality lookup map from items.json at module load time: itemID -> quality (0..6)
const QUALITY_MAP = (() => {
  try {
    const m = {};
    for (const it of itemsData) {
      if (it && typeof it.itemID !== 'undefined') {
        const q = (typeof it.Quality === 'number') ? it.Quality
                 : (typeof it.quality === 'number') ? it.quality
                 : (typeof it.ItemQuality === 'number') ? it.ItemQuality
                 : (typeof it.rarity === 'number') ? it.rarity
                 : null;
  // Add +1 so indices align with QUALITY_BORDER_COLORS layout
  if (typeof q === 'number') m[it.itemID] = q + 1;
      }
    }
    return m;
  } catch (_e) {
    return {};
  }
})();

// Build an icon URL lookup from items.json: itemID -> iconUrl
const ITEM_ICON_MAP = (() => {
  try {
    const m = {};
    for (const it of itemsData) {
      if (it && typeof it.itemID !== 'undefined') {
        if (it.iconUrl) m[it.itemID] = String(it.iconUrl).replace(/^https?:\/\/cdn\.cavernoftime\.com\//, '/cot/cdn/');
        else if (it.IconUrl) m[it.itemID] = String(it.IconUrl).replace(/^https?:\/\/cdn\.cavernoftime\.com\//, '/cot/cdn/');
  else if (it.icon) m[it.itemID] = `/cot/cdn/wotlk/icons/large/${it.icon}.jpg`;
      }
    }
    return m;
  } catch (_e) { return {}; }
})();

// Build an item level lookup from items.json: itemID -> ItemLevel
const ITEM_ILVL_MAP = (() => {
  try {
    const m = {};
    for (const it of itemsData) {
      if (it && typeof it.itemID !== 'undefined') {
        const il = (typeof it.ItemLevel === 'number') ? it.ItemLevel
                 : (typeof it.itemLevel === 'number') ? it.itemLevel
                 : (typeof it.ItemLevel === 'string' && !isNaN(parseInt(it.ItemLevel,10))) ? parseInt(it.ItemLevel,10)
                 : null;
        if (typeof il === 'number') m[it.itemID] = il;
      }
    }
    return m;
  } catch (_e) { return {}; }
})();

// Use the static CSS file served from public (root)
const COT_CSS_PATH = '/Tooltip.css';

function CharacterPanel({ character, error, loading, collapseSignal }) {
  const [itemDetails, setItemDetails] = useState({}); // id -> { raw, icon, quality, status, attempts }
  const [activityDetails, setActivityDetails] = useState({}); // key(kind:id) -> { raw, tooltip, status }
  const [guildRank, setGuildRank] = useState(null); // parsed from guild summary page
  const tooltipFrameRef = useRef(null);
  const [hoverPos, setHoverPos] = useState({x:0,y:0,visible:false,content:'',height:0,width:0,itemId:null,actKey:null});
  const inFlightRef = useRef({});
  const actInFlightRef = useRef({});
  // In-memory cache for recent CoT results to avoid repeated parsing and localStorage reads
  const cacheRef = useRef({});
  const MAX_RETRIES = 3;
  const CACHE_TTL_MS = 24*60*60*1000; // 24 hours
  const LS_LEFT_KEY = 'gc-stats-left';
  const LS_RIGHT_KEY = 'gc-stats-right';

  // Stats dropdown selections (persisted)
  const [leftStatsType, setLeftStatsType] = useState('Base Stats');
  const [rightStatsType, setRightStatsType] = useState('Melee');
  useEffect(()=>{
    try {
      const l = localStorage.getItem(LS_LEFT_KEY);
      const r = localStorage.getItem(LS_RIGHT_KEY);
      if(l) setLeftStatsType(l);
      if(r) {
        setRightStatsType(r);
      } else {
        // No saved preference: default casters to 'Spell'
        const cls = (character?.class || '').toLowerCase();
        if(cls === 'mage' || cls === 'priest' || cls === 'warlock') {
          setRightStatsType('Spell');
        }
      }
    } catch(_e){}
  // include only character.class to re-evaluate default when character changes
  // but keep from overwriting any saved preference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[character?.class]);
  useEffect(()=>{ try { localStorage.setItem(LS_LEFT_KEY, leftStatsType); } catch(_e){} }, [leftStatsType]);
  useEffect(()=>{ try { localStorage.setItem(LS_RIGHT_KEY, rightStatsType); } catch(_e){} }, [rightStatsType]);

  // Base icon URL; we replace the file name after the last "/" with the parsed icon name
  const DEFAULT_ICON_URL = '/cot/cdn/wotlk/icons/large/inv_misc_questionmark.jpg';

  // class-based background image behind equipment slots
  const getClassBackground = (cls) => {
    const key = (cls || '').toString().trim().toLowerCase();
    const map = {
      'death knight': 'DressingRoomDeathKnight.png',
      'druid': 'DressingRoomDruid.png',
      'hunter': 'DressingRoomHunter.png',
      'mage': 'DressingRoomMage.png',
      'paladin': 'DressingRoomPaladin.png',
      'priest': 'DressingRoomPriest.png',
      'rogue': 'DressingRoomRogue.png',
      'shaman': 'DressingRoomShaman.png',
      'warlock': 'DressingRoomWarlock.png',
      'warrior': 'DressingRoomWarrior.png',
    };
    const file = map[key];
    if (!file) return null;
    try {
      return require(`../assets/dressing_rooms/${file}`);
    } catch (_e) {
      return null;
    }
  };
  const classBgSrc = getClassBackground(character?.class);

  // Map slotType to CSS class names used
  const slotClassMap = {
    'Head':'head','Neck':'neck','Shoulders':'shoulders','Back':'back','Chest':'chest','Shirt':'shirt','Tabard':'tabard','Wrist':'wrist',
    'Hand':'hand','Waist':'waist','Legs':'legs','Feet':'feet','Ring 1':'ring1','Ring 2':'ring2','Trinket 1':'trinket1','Trinket 2':'trinket2',
    'Main hand':'mainhand','Off hand':'offhand','Relic':'relic'
  };

  // Map slot class -> { x, y } in pixels on the equipment-overview sprite (50x50 sections)
  const slotSpritePositions = {
    head: { x: 2, y: 3 },
    neck: { x: 2, y: 58 },
    shoulders: { x: 2, y: 113 },
    back: { x: 2, y: 168 },
    chest: { x: 2, y: 223 },
    shirt: { x: 2, y: 278 },
    tabard: { x: 2, y: 333 },
    wrist: { x: 2, y: 388 },

    mainhand: { x: 143, y: 457 },
    offhand: { x: 198, y: 457 },
    relic: { x: 253, y: 457 },

    hand: { x: 392, y: 3 },
    waist: { x: 392, y: 58 },
    legs: { x: 392, y: 113 },
    feet: { x: 392, y: 168 },
    ring1: { x: 392, y: 223 },
    ring2: { x: 392, y: 278 },
    trinket1: { x: 392, y: 333 },
    trinket2: { x: 392, y: 388 }
  };

  // Caching in localStorage (24h) to avoid repeat fetch hits
  // Follow-ups implemented: sanitization, abort controllers, dynamic sizing, debug toggle, item level parsing
  const DEBUG = process.env.NODE_ENV === 'development';

  // --- Helper parsing, sanitization & network functions ---
  const unescapeJsString = useCallback((str)=>{
    if(!str) return '';
    return str
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\\//g, '/')
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"');
  }, []);

  const sanitizeCotHtml = useCallback((html)=>{
    if(!html) return '';
    let safe = html;
    // Remove script, iframe, object, embed tags
    safe = safe.replace(/<script[^>]*>[\s\S]*?<\/script>/gi,'')
               .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi,'')
               .replace(/<object[^>]*>[\s\S]*?<\/object>/gi,'')
               .replace(/<embed[^>]*>[\s\S]*?<\/embed>/gi,'');
    // Strip on* event handlers
    safe = safe.replace(/ on[a-z]+="[^"]*"/gi,'');
    // Inline JS URLs
    safe = safe.replace(/javascript:/gi,'');
    return safe;
  }, []);

  // Inline small images (e.g., money icons) referenced in CoT tooltip HTML.
  // This fetches the image once, converts to data URL and caches in localStorage to avoid repeated image requests.
  const ensureImagesInlined = useCallback(async (html) => {
    if(!html) return html;
  // Match common cot image URLs (cdn.cavernoftime.com/images/... or icons/...)
  // Use RegExp constructor string to avoid unnecessary escaping inside a literal
  // allow word chars, slash, dot and dash (dash placed at end of class to avoid needing an escape)
  const imgRegex = new RegExp('https?://(?:cdn\\.)?cavernoftime\\.com/[\\w/.-]+\\.(?:png|gif|jpg)', 'gi');
  const matches = Array.from(new Set((html.match(imgRegex) || [])));
    if(matches.length === 0) return html;
    const promises = matches.map(async (url) => {
      const key = `cot_img_${url}`;
      try {
        const cached = localStorage.getItem(key);
        if(cached){
          const parsed = JSON.parse(cached);
          if(Date.now() - parsed.ts < CACHE_TTL_MS && parsed.dataUrl) return { url, dataUrl: parsed.dataUrl };
        }
      } catch(_e){}
      try {
        const proxied = url.replace(/^https?:\/\/cdn\.cavernoftime\.com\//, '/cot/cdn/');
        const resp = await fetch(proxied);
        if(!resp.ok) throw new Error('img fetch failed');
        const blob = await resp.blob();
        return await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => {
            const dataUrl = fr.result;
            try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), dataUrl })); } catch(_e){}
            resolve({ url, dataUrl });
          };
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
      } catch(_e){ return { url, dataUrl: null }; }
    });
    const results = await Promise.all(promises);
    let out = html;
    results.forEach(r => { if(r.dataUrl){ out = out.split(r.url).join(r.dataUrl); } });
    return out;
  }, [CACHE_TTL_MS]);

  const parseItemHTML = useCallback((html)=>{
    let icon = null; let quality = null; let itemLevel = null; let tooltip = null;

    // 1) Most common inlined JSON
    const iconMatchJson = html.match(/"icon"\s*:\s*"([^"]+)"/i);
    if(iconMatchJson) icon = iconMatchJson[1];

    // 2) JS object style icon: 'inv_...'
    if(!icon){
      const iconMatchJs = html.match(/(?:^|[^a-z])icon\s*:\s*['"]?([a-z0-9_-]+)['"]?/i);
      if(iconMatchJs) icon = iconMatchJs[1];
    }

    // 3) Fallback: find from CSS/image paths inside HTML
    if(!icon){
      const iconCss = html.match(/icons\/(?:large|medium|tiny)\/([a-z0-9_-]+)\.\w+/i);
      if(iconCss) icon = iconCss[1];
    }

    const qualityMatch = html.match(/"quality"\s*:\s*(\d)/) || html.match(/class="q(\d)"/);
    if(qualityMatch) quality = parseInt(qualityMatch[1],10);

    const ilvlMatch = html.match(/Item Level\s*(\d{1,3})/i);
    if(ilvlMatch) itemLevel = parseInt(ilvlMatch[1],10);

    // Extract tooltip HTML: tooltip_enus: '<table>...'</table>'
    const tooltipMatch = html.match(/tooltip_enus\s*:\s*'((?:\\'|[\s\S])*?)'/i);
    if(tooltipMatch){
      tooltip = unescapeJsString(tooltipMatch[1]);
      // Normalize excessive breaks and spacing between adjacent tables to avoid odd line gaps
      // Use RegExp constructed from strings to avoid unnecessary escape lint warnings
      const tableJoinRe = new RegExp('</table>\\s*(?:<br\\s*/?>)?\\s*<table', 'gi');
      const brCollapseRe = new RegExp('(?:<br\\s*/?>\\s*){2,}', 'gi');
      tooltip = tooltip.replace(tableJoinRe, '</table><table').replace(brCollapseRe, '<br/>');
    }

    return { icon, quality, itemLevel, tooltip };
  }, [unescapeJsString]);

  
  // Achievement-specific parser: CoT registers achievements with a different
  // JS object shape (name_enus, icon, tooltip_enus). Extract only the table
  // HTML blocks (title + description + criteria) and return them.
  const parseAchievementHTML = useCallback((html) => {
    let icon = null; let name = null; let tooltip = '';

    try {
      const nameMatch = html.match(/name_enus\s*:\s*['"]((?:\\.|[^'"\\])*)['"]/i);
      if (nameMatch) name = unescapeJsString(nameMatch[1]);

      const iconMatch = html.match(/icon\s*:\s*['"]([a-z0-9_-]+)['"]/i);
      if (iconMatch) icon = iconMatch[1];

      // Try to capture tooltip_enus JS string (single- or double-quoted)
      const tooltipMatch = html.match(/tooltip_enus\s*:\s*(['"])((?:\\.|[\s\S])*?)\1/i);
      if (tooltipMatch) {
        tooltip = unescapeJsString(tooltipMatch[2]);
      } else {
        // DOM fallback: parse HTML and collect table blocks
        try {
          const dp = new DOMParser();
          const doc = dp.parseFromString(html, 'text/html');
          const cav = doc.querySelector('.cavernoftime-tt');
          if (cav) {
            tooltip = cav.innerHTML || '';
          } else {
            const tables = doc.querySelectorAll('table');
            if (tables && tables.length) {
              tooltip = Array.from(tables).map(t => t.outerHTML).join('');
            }
          }
        } catch (_e) {
          tooltip = '';
        }
      }

      // Keep only <table>...</table> blocks; discard any surrounding markup
      if (tooltip) {
        const tableRe = new RegExp('<table[\\s\\S]*?</table>', 'gi');
        const matches = tooltip.match(tableRe);
          if (matches && matches.length) {
          tooltip = matches.join('');
          // Normalize accidental <br> between tables (use RegExp from string to avoid lint escapes)
          const tableJoinRe = new RegExp('</table>\\s*(?:<br\\s*/?>)?\\s*<table', 'gi');
          tooltip = tooltip.replace(tableJoinRe, '</table><table');
        } else {
          tooltip = '';
        }
      }
    } catch (_e) {
      // ignore and return empty tooltip
      tooltip = '';
    }

    return { icon, name, tooltip };
  }, [unescapeJsString]);

  function handleLoadError(id){
    setItemDetails(prev=>{
      const existing = prev[id] || { attempts:0 };
      const attempts = (existing.attempts || 0) + 1;
      return { ...prev, [id]: { ...existing, status:'error', attempts } };
    });
    const attemptsForDelay = (itemDetails[id]?.attempts || 0) + 1;
    const delay = Math.min(1500 * Math.pow(2, attemptsForDelay - 1), 10000);
    setTimeout(()=>{
      setItemDetails(current=>{
        const e = current[id];
        if(!e) return current;
        if(e.status === 'error' && e.attempts < MAX_RETRIES){
          queueFetch(id);
        }
        return current;
      });
    }, delay);
  }

  // Keep a ref to the latest handleLoadError so we don't need it as a dependency
  const handleLoadErrorRef = useRef(handleLoadError);
  useEffect(()=>{ handleLoadErrorRef.current = handleLoadError; });

  const queueFetch = useCallback((id, params)=>{
    if(inFlightRef.current[id]) return;
    // Build cache key consistent with existing localStorage usage
    const query = new URLSearchParams({ power: 'true', ...(params||{}) }).toString();
    if (DEBUG) {
      try {
        console.debug('[CoT][queueFetch]', { id, params, url: `/cot/item/${id}?${query}` });
      } catch(_){}
    }
    const cacheKey = `cot_item_${id}_${query}`;
    // 1) Check in-memory cache first
    try {
      const mem = cacheRef.current[cacheKey];
      if(mem && (Date.now() - mem.ts) < CACHE_TTL_MS){
        // Populate itemDetails from memory cache
        setItemDetails(prev=>({...prev,[id]:{...mem.data, status:'ok', attempts: mem.data.attempts || 0}}));
        // If the user is currently hovering this item, update the tooltip content immediately
        try { setHoverPos(h => { if(h.visible && h.itemId === id){ const content = mem.data.tooltip || mem.data.raw || ''; return { ...h, content }; } return h; }); } catch(_e){}
        return;
      }
    } catch(_e){}
    // 2) Check localStorage cache
    try {
      const cached = localStorage.getItem(cacheKey);
        if(cached){
        const parsed = JSON.parse(cached);
        if(Date.now() - parsed.ts < CACHE_TTL_MS){
          cacheRef.current[cacheKey] = parsed; // warm memory
          setItemDetails(prev=>({...prev,[id]:{...parsed.data, status:'ok', attempts: parsed.data.attempts || 0}}));
          // Inline any CoT images (money icons) before showing tooltip
          try {
            const content = parsed.data.tooltip || parsed.data.raw || '';
            ensureImagesInlined(content).then(inlined => {
              setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: inlined }; } return h; });
            }).catch(()=>{
              setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content }; } return h; });
            });
          } catch(_e){}
          return;
        }
      }
    } catch(_e){}
    const controller = new AbortController();
    inFlightRef.current[id] = { controller };
    setItemDetails(prev=>{
      const existing = prev[id] || {};
      return ({...prev,[id]:{...existing,status:'loading',attempts: existing.attempts || 0}});
    });
    const url = `/cot/item/${id}?${query}`;
    
    fetch(url,{ signal: controller.signal })
      .then(r=>{ if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(html => {
        // Parse from raw HTML before sanitization so we can read values present in <script> blocks
        const parsed = parseItemHTML(html);
        const sanitized = sanitizeCotHtml(html);
  // update memory cache and localStorage
  const data = { ...parsed, raw: sanitized };
  try { cacheRef.current[cacheKey] = { ts: Date.now(), data }; } catch(_e){}
  try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data })); } catch(_e){}
        // Use functional updater to read previous attempts without referencing outer itemDetails
        setItemDetails(prev=>{
          const attempts = prev[id]?.attempts || 0;
          return ({...prev,[id]:{...data, status:'ok', attempts}});
        });
        // If the user is still hovering this item, inline images then update the hover content so the tooltip appears immediately
        try {
          const contentSrc = parsed.tooltip || sanitized || parsed.raw || '';
          ensureImagesInlined(contentSrc).then(inlined => {
            setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: inlined }; } return h; });
          }).catch(()=>{
            setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: contentSrc }; } return h; });
          });
        } catch(_e){}
      })
      .catch(e=>{ if(e.name !== 'AbortError'){ if(DEBUG) console.warn('[CoT] Load error', id, e); handleLoadErrorRef.current && handleLoadErrorRef.current(id);} })
      .finally(()=>{ delete inFlightRef.current[id]; });
  }, [DEBUG, parseItemHTML, sanitizeCotHtml, CACHE_TTL_MS, ensureImagesInlined]);

  // Fetch CoT tooltip for a generic activity entry (e.g., achievement)
  const queueFetchActivity = useCallback((kind, id)=>{
    const key = `${kind}:${id}`;
    if(actInFlightRef.current[key]) return;
    const cacheKey = `cot_act_${kind}_${id}`;
    // memory/local cache
    try {
      const mem = cacheRef.current[cacheKey];
      if(mem && (Date.now() - mem.ts) < CACHE_TTL_MS){
        setActivityDetails(prev=>({ ...prev, [key]: { ...mem.data, status:'ok' } }));
        // Update hover content if currently showing this activity
        setHoverPos(h => { if(h.visible && h.actKey === key){ return { ...h, content: mem.data.tooltip || mem.data.raw || '' }; } return h; });
        return;
      }
    } catch(_e){}
    try {
      const cached = localStorage.getItem(cacheKey);
      if(cached){
        const parsed = JSON.parse(cached);
        if(Date.now() - parsed.ts < CACHE_TTL_MS){
          cacheRef.current[cacheKey] = parsed;
          setActivityDetails(prev=>({ ...prev, [key]: { ...parsed.data, status:'ok' } }));
          const content = (parsed.data.tooltip || parsed.data.raw || '');
          ensureImagesInlined(content).then(inlined => {
            setHoverPos(h => { if(h.visible && h.actKey === key){ return { ...h, content: inlined }; } return h; });
          }).catch(()=>{
            setHoverPos(h => { if(h.visible && h.actKey === key){ return { ...h, content }; } return h; });
          });
          return;
        }
      }
    } catch(_e){}
    const controller = new AbortController();
    actInFlightRef.current[key] = { controller };
    setActivityDetails(prev=>({ ...prev, [key]: { ...(prev[key]||{}), status:'loading' } }));
    const url = `/cot/achievement/${id}?power=true`;
    fetch(url, { signal: controller.signal })
      .then(r => { if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(html => {
        const parsed = parseAchievementHTML(html); // will extract tooltip_enus
        const sanitized = sanitizeCotHtml(html);
        const data = { ...parsed, raw: sanitized };
        try { cacheRef.current[cacheKey] = { ts: Date.now(), data }; } catch(_e){}
        try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data })); } catch(_e){}
        setActivityDetails(prev=>({ ...prev, [key]: { ...data, status:'ok' } }));
        const contentSrc = parsed.tooltip || sanitized || '';
        ensureImagesInlined(contentSrc).then(inlined => {
          setHoverPos(h => { if(h.visible && h.actKey === key){ return { ...h, content: inlined }; } return h; });
        }).catch(()=>{
          setHoverPos(h => { if(h.visible && h.actKey === key){ return { ...h, content: contentSrc }; } return h; });
        });
      })
      .catch(_e => {
        setActivityDetails(prev=>({ ...prev, [key]: { ...(prev[key]||{}), status:'error' } }));
      })
      .finally(()=>{ delete actInFlightRef.current[key]; });
  }, [CACHE_TTL_MS, ensureImagesInlined, parseAchievementHTML, sanitizeCotHtml]);

  function manualRetry(id, ev, params){ ev?.stopPropagation(); queueFetch(id, params); }

  useEffect(()=>{
    if(!character || !Array.isArray(character.equipment)) return;
    character.equipment.forEach(eq=>{
      if(!eq || !eq.item) return;
      const id = eq.item;
      if(itemDetails[id] && itemDetails[id].status === 'ok') return;
      const params = {};
      if (eq.ench) params.ench = String(eq.ench);
      if (Array.isArray(eq.gems) && eq.gems.length) {
        const gemsArr = eq.gems.map(g => g || 0);
        while (gemsArr.length < 3) gemsArr.push(0);
        params.gems = gemsArr.slice(0, 3).join(':');
      }
      // Include transmog/display id when available so the fetched tooltip matches the visual
      if (eq.transmog) params.transmog = String(eq.transmog);
      const query = new URLSearchParams({ power: 'true', ...(params||{}) }).toString();
      const cacheKey = `cot_item_${id}_${query}`;
      try {
        const cached = localStorage.getItem(cacheKey);
        if(cached){
          const parsed = JSON.parse(cached);
          if(Date.now() - parsed.ts < 24*60*60*1000) {
            if(parsed.data && parsed.data.tooltip){
              setItemDetails(prev=>({...prev,[id]:{...parsed.data, status:'ok', attempts:parsed.data.attempts || 0}}));
              return;
            }
          }
        }
      }catch(_e){}
      // Do not prefetch on character load; defer fetching to first hover to reduce network usage.
    });
  },[character, itemDetails, queueFetch]);

  // When the character changes (search or refresh), collapse any expanded raid rows
  useEffect(()=>{
    setExpandedRaids({});
    // Clear any previously cached raid boss data so expanding will re-fetch for the new character
    setRaidBossData({});
    setRaidLoading({});
    setRaidError({});
  },[character?.name, character?.realm]);

  // Fetch guild rank once per character using the guild summary page
  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        setGuildRank(null);
        if (!character || !character.name || !character.realm || !character.guild) return;
        const name = String(character.name);
        const realm = String(character.realm);
        const guild = String(character.guild);
        // Cache key
        const key = `gc_guild_rank|${guild}|${realm}|${name}`;
        // Target href to locate the character row in the guild summary HTML
        const targetHref = `/character/${encodeURIComponent(name)}/${encodeURIComponent(realm)}/profile`;
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const obj = JSON.parse(cached);
            if (obj && obj.rank && (Date.now() - (obj.ts || 0) < 24*60*60*1000)) {
              if (!cancelled) setGuildRank(obj.rank);
              return;
            }
          }
        } catch(_) {}

        const url = `/armory/guild/${encodeURIComponent(guild)}/${encodeURIComponent(realm)}/summary/${encodeURIComponent(name)}`;
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 15000);
        try {
          const res = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'text/html' } });
          const html = await res.text();
          // Prefer DOM parsing when available
          let rank = null;
          try {
            if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
              const dp = new window.DOMParser();
              const doc = dp.parseFromString(html, 'text/html');
              const anchors = Array.from(doc.querySelectorAll('a[href]'));
              const lowerTarget = targetHref.toLowerCase();
              let row = null;
              for (const a of anchors) {
                const h = String(a.getAttribute('href') || '').trim();
                if (!h) continue;
                const hl = h.toLowerCase();
                if (hl.endsWith(lowerTarget) || hl.includes(lowerTarget)) {
                  row = a.closest('tr');
                  if (row) break;
                } else {
                  // Flexible match: split and compare segments ignoring case
                  try {
                    const parts = hl.split('/').filter(Boolean);
                    const i = parts.indexOf('character');
                    if (i !== -1 && parts[i+1] && parts[i+2]) {
                      const nm = decodeURIComponent(parts[i+1]).toLowerCase();
                      const rl = decodeURIComponent(parts[i+2]).toLowerCase();
                      if (nm === name.toLowerCase() && rl === realm.toLowerCase()) {
                        if (parts[i+3] && parts[i+3].startsWith('profile')) {
                          row = a.closest('tr');
                          if (row) break;
                        }
                      }
                    }
                  } catch(_) {}
                }
              }
              if (row) {
                const tds = row.querySelectorAll('td');
                // Guild rank is in the next cell (6th <td>) on Warmane guild summary rows
                if (tds && tds[5]) {
                  rank = (tds[5].textContent || '').replace(/\u00A0/g,' ').replace(/\s+/g,' ').trim() || null;
                }
              }
            }
          } catch(_) {}
          // Regex fallback: find the row containing the specific anchor and capture 5th td
          if (!rank) {
            try {
              const esc = targetHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              const rowRe = new RegExp(`<tr[\\s\\S]*?<a[^>]+href=["']${esc}["'][\\s\\S]*?</tr>`, 'i');
              const rm = html.match(rowRe);
              if (rm && rm[0]) {
                const rowHtml = rm[0];
                // capture all tds
                const tdRe = /<td[^>]*>([\s\S]*?)<\/td>/gi;
                let m; let idx = 0;
                while ((m = tdRe.exec(rowHtml)) !== null) { idx++; if (idx === 6) { const txt = m[1].replace(/<[^>]+>/g,' ').replace(/&nbsp;|&amp;/g,' ').replace(/\s+/g,' ').trim(); rank = txt || null; break; } }
              }
            } catch(_) {}
          }
          if (!cancelled && rank) {
            setGuildRank(rank);
            try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), rank })); } catch(_) {}
          }
        } finally { clearTimeout(t); }
      } catch(_) { /* ignore fetch/parsing errors */ }
    }
    run();
    return () => { cancelled = true; };
  }, [character]);

  // Also collapse when an external UI (e.g., HistoryPanel) requests it via collapseSignal
  useEffect(()=>{
    if(typeof collapseSignal !== 'undefined'){
      setExpandedRaids({});
      // also clear any cached raid data so next expand will fetch fresh
      setRaidBossData({});
      setRaidLoading({});
      setRaidError({});
    }
  },[collapseSignal]);

  // Abort inflight requests on unmount
  useEffect(()=>{
    const inflight = inFlightRef.current; // capture the object (mutated in place)
    const actInflight = actInFlightRef.current;
    return () => {
      Object.values(inflight).forEach(entry=>{ try { entry.controller && entry.controller.abort(); } catch(_e){} });
      Object.values(actInflight).forEach(entry=>{ try { entry.controller && entry.controller.abort(); } catch(_e){} });
    };
  },[]);

  // Hover handlers: fixed-size tooltip per item; only position updates on mouse move
  function clampToViewport(x, y, w, h){
    const pad = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
    let nx = Math.max(pad, Math.min(x, vw - w - pad));
    let ny = Math.max(pad, Math.min(y, vh - h - pad));
    return {x:nx, y:ny};
  }

  const handleItemEnter = (e, eq) => {
    if(!eq || !eq.item) return; // don't show tooltip for empty slots
    // Debug: print the Warmane rel attribute captured during enrichment
    if (DEBUG) {
      try {
        console.debug('[Hover][enter]', {
          slot: eq.slotType,
          item: eq.item,
          ench: eq.ench,
          gems: Array.isArray(eq.gems) ? eq.gems : undefined,
          rel: eq.rel || null
        });
      } catch(_){}
    }
    // Build requested variant params and decide whether to (re)fetch
    const params = {};
    if (eq.ench) params.ench = String(eq.ench);
    if (Array.isArray(eq.gems) && eq.gems.length) {
      const gemsArr = eq.gems.map(g => g || 0);
      while (gemsArr.length < 3) gemsArr.push(0);
      params.gems = gemsArr.slice(0, 3).join(':');
    }
    // Prefer transmog if present so the variant requested matches the model/appearance
    if (eq.transmog) params.transmog = String(eq.transmog);
    const signature = new URLSearchParams({ power: 'true', ...(params||{}) }).toString();
    const cacheKey = `cot_item_${eq.item}_${signature}`;
    let variantFromCache = null;
    try {
      const mem = cacheRef.current[cacheKey];
      if (mem && (Date.now() - mem.ts) < CACHE_TTL_MS) variantFromCache = mem.data;
      if (!variantFromCache) {
        const ls = localStorage.getItem(cacheKey);
        if (ls) {
          const parsed = JSON.parse(ls);
          if (Date.now() - parsed.ts < CACHE_TTL_MS) variantFromCache = parsed.data;
        }
      }
    } catch(_){}
    const existing = itemDetails[eq.item];
    const needFetch = (!existing || existing.status !== 'ok' || !variantFromCache);
    if (needFetch) {
      if (DEBUG) {
        try { console.debug('[Hover][params]', { item: eq.item, params, reason: !existing || existing.status !== 'ok' ? 'no-existing' : 'variant-miss' }); } catch(_){}
      }
      queueFetch(eq.item, params, eq.rel);
    }
    const detail = variantFromCache || itemDetails[eq.item];
    const content = detail ? (detail.tooltip || detail.raw) : '';
    // Start with provisional size; will be measured and locked after render
    const provisional = { width: 360, height: 240 };
    const pos = clampToViewport(e.clientX+15, e.clientY+15, provisional.width, provisional.height);
    setHoverPos({ x: pos.x, y: pos.y, visible:true, content, width: provisional.width, height: provisional.height, itemId: eq.item, actKey: null });
  };

  const handleItemMove = (e) => {
    setHoverPos(h => {
      if(!h.visible) return h;
      const pos = clampToViewport(e.clientX+15, e.clientY+15, h.width || 0, h.height || 0);
      if(pos.x === h.x && pos.y === h.y) return h; // no re-render if unchanged
      return { ...h, x: pos.x, y: pos.y };
    });
  };

  const handleItemLeave = ()=> setHoverPos(h=>({...h,visible:false,itemId:null,actKey:null}));

  // Activity hover handlers (e.g., achievements)
  const handleActivityEnter = (e, act) => {
    const key = `${(act.type||'achievement')}:${act.id}`;
    if(act && act.id && (activityDetails[key]?.status !== 'ok')){
      queueFetchActivity(act.type || 'achievement', act.id);
    }
    const detail = activityDetails[key];
    const content = detail ? (detail.tooltip || detail.raw) : '';
    const provisional = { width: 360, height: 200 };
    const pos = clampToViewport(e.clientX+15, e.clientY+15, provisional.width, provisional.height);
    setHoverPos({ x: pos.x, y: pos.y, visible:true, content, width: provisional.width, height: provisional.height, itemId: null, actKey: key });
  };

  const handleActivityMove = (e) => { handleItemMove(e); };
  const handleActivityLeave = () => { handleItemLeave(); };

  // Status icon hover: show the first missing item's tooltip (if any)
  const getMissingSlotLabels = (type) => {
    const arr = type === 'gems' ? (Array.isArray(character?.missingGems) ? character.missingGems : [])
                                : (Array.isArray(character?.missingEnchants) ? character.missingEnchants : []);
    // Normalize entries to strings (slot labels)
    return arr.map(s => (typeof s === 'string') ? s : (s && s.slot ? s.slot : ''))
              .filter(Boolean);
  };

  const [statusHover, setStatusHover] = useState({ visible:false, x:0, y:0, type:null, items:[] });
  const statusPopoverRef = useRef(null);
  const equipmentSlotsRef = useRef(null);
  const equipmentLeftRef = useRef(null);
  const equipmentRightRef = useRef(null);
  const headerGridRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(null);
  const [expandedRaids, setExpandedRaids] = useState({});
  const [raidBossData, setRaidBossData] = useState({}); // raidKey -> { bossName -> {10NM,10HC,25NM,25HC} }
  const [raidLoading, setRaidLoading] = useState({});
  const [raidError, setRaidError] = useState({});
  // (previously used for raid hover/popover) removed unused refs

  // Measure header stat cards and set a uniform height equal to the largest
  useEffect(()=>{
    const measure = ()=>{
      try{
        if(!headerGridRef.current) return;
        const cards = headerGridRef.current.querySelectorAll('.header-stat-card');
        let max = 0;
        cards.forEach(c => {
          const r = c.getBoundingClientRect();
          if(r.height > max) max = Math.ceil(r.height);
        });
        if(max && max !== cardHeight) setCardHeight(max);
      }catch(_e){}
    };
    measure();
    window.addEventListener('resize', measure);
    // re-measure when images inside header load
    const imgs = headerGridRef.current ? headerGridRef.current.querySelectorAll('img') : [];
    imgs.forEach(img => img.addEventListener('load', measure));
    return ()=>{
      window.removeEventListener('resize', measure);
      imgs.forEach(img => img.removeEventListener('load', measure));
    };
  }, [character?.gearScore, character?.achievementpoints, character?.missingGems?.length, character?.missingEnchants?.length, cardHeight]);

  // Measure equipment columns so we can crop the class background image
  const [classBgStyle, setClassBgStyle] = useState(null);
  useEffect(()=>{
    function measureBg(){
      try{
        const container = equipmentSlotsRef.current;
        const left = equipmentLeftRef.current;
        const right = equipmentRightRef.current;
        if(!container || !left || !right){ setClassBgStyle(null); return; }
        const crect = container.getBoundingClientRect();
        const lrect = left.getBoundingClientRect();
        const rrect = right.getBoundingClientRect();
  // left offset relative to container
  const leftOffset = Math.max(0, Math.round(lrect.left - crect.left));
  // right offset relative to container (space to keep on right)
  const rightOffset = Math.max(0, Math.round(crect.right - rrect.right));
  // Use clip-path inset to precisely crop the image inside its bounding box.
  // Keep the image anchored to the container edges (inset:0) and then clip
  // from the left/right by the computed pixel offsets.
  setClassBgStyle({ position:'absolute', top:0, left:0, right:0, bottom:0, width:'100%', height:'100%', clipPath: `inset(0px ${rightOffset}px 0px ${leftOffset}px)` });
      }catch(_e){ setClassBgStyle(null); }
    }
    // measure initially and on changes
    measureBg();
    window.addEventListener('resize', measureBg);
    return () => { window.removeEventListener('resize', measureBg); };
  }, [classBgSrc, character?.equipment, character?.class]);

  const handleStatusEnter = (e, type) => {
    const labels = getMissingSlotLabels(type);
    // Build items to display in the popover. If there are missing slots, show them.
    // If none, show a single friendly 'All items ...' message for the hovered type.
    let itemsToShow = [];
    if (labels.length) {
      itemsToShow = labels;
    } else {
      itemsToShow = [ type === 'gems' ? 'All items gemmed' : 'All items enchanted' ];
    }
    // Show list popover anchored near the icon
    const tgt = e.currentTarget;
    try {
      const rect = tgt.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const isMobile = vw <= 768;
      const x = isMobile ? rect.left : (rect.right + 8);
      const y = rect.top;
      setStatusHover({ visible:true, x, y, type, items: itemsToShow });
    } catch(_e) {
      setStatusHover({ visible:true, x: e.clientX + 15, y: e.clientY + 15, type, items: itemsToShow });
    }
    // Ensure no item tooltip shows while using status popover
    handleItemLeave();
  };

  const handleStatusLeave = () => {
    setStatusHover(h => ({ ...h, visible:false }));
    handleItemLeave();
  };

  // Map raid key to Warmane 'category' form value
  const RAID_CATEGORY = { 'ICC': '15062', 'RS': '15062', 'TOC': '15021', 'ULDUAR': '14963' };

  // Boss lists per raid (display order)
  const RAID_BOSSES = {
    'ICC': [
      'Lord Marrowgar kills',
      'Lady Deathwhisper kills',
      'Gunship Battle victories',
      'Deathbringer kills',
      'Festergut kills',
      'Rotface kills',
      'Professor Putricide kills',
      'Blood Prince Council kills',
      'Blood Queen Lana\'thel kills',
      'Valithria Dreamwalker rescues',
      'Sindragosa kills',
      'Victories over the Lich King'
    ],
    'RS': [ 
      'Halion' 
    ],
    'TOC': [ 
      'Victories over the Beasts of Northrend',
      'Lord Jaraxxus kills',
      'Victories over the Faction Champions',
      'Val\'kyr Twins kills',
      'Times completed the Trial of the Crusader' 
    ],
    'ULDUAR': [ 
      'Flame Leviathan kills',
      'Ignis the Furnace Master kills',
      'Razorscale kills',
      'XT-002 Deconstructor kills',
      'Assembly of Iron kills',
      'Kologarn kills',
      'Auriaya kills',
      'Freya victories',
      'Hodir victories',
      'Mimiron victories',
      'Thorim vicitories',
      'General Vezax kills',
      'Yogg-Saron kills',
      'Algalon the Observer kills' 
    ]
  };
  
  // Parse the HTML returned by the /armory/statistics proxy and extract boss counters
  // Use the shared parser in src/lib/parseRaids.js to extract rows and instance codes
  // parseTable returns rows: { boss, code, value, rawDesc, rawValue }
  // We'll map those into the expected output shape for the UI (per bossesList)
  function parseRaidStatisticsHtml(html, bossesList, raidKey){
    const out = {};
    try{
      bossesList.forEach(b => { out[b] = { '10NM': '- -', '10HC': '- -', '25NM': '- -', '25HC': '- -' }; });
      if(!html) return out;
      // Lazy-load the parser to avoid bundling issues in environments that disallow dynamic imports
      // (This also keeps behaviour identical to previous implementation if import fails)
      let parseTableFn = null;
      let parseTableTocFn = null;
  let parseTableUldFn = null;
      try{
        // Prefer the ES module under src/lib
        // eslint-disable-next-line global-require, import/no-dynamic-require
        const pr = require('../lib/parseRaidStatistics');
        // Assign explicitly to avoid mixing || and && in a single expression (ESLint no-mixed-operators)
        if (pr) {
          if (pr.parseTable) parseTableFn = pr.parseTable;
          else if (pr.default && pr.default.parseTable) parseTableFn = pr.default.parseTable;
          else parseTableFn = pr.parseTable || pr.default;
        }
        if (raidKey === 'TOC') {
          try {
            // eslint-disable-next-line global-require, import/no-dynamic-require
            const pt = require('../lib/parseRaidTOC');
            if (pt) {
              if (pt.parseTableToc) parseTableTocFn = pt.parseTableToc;
              else if (pt.default && pt.default.parseTableToc) parseTableTocFn = pt.default.parseTableToc;
            }
          } catch(_inner) { /* ignore, will fall back */ }
        }
        if (raidKey === 'ULDUAR') {
          try {
            // eslint-disable-next-line global-require, import/no-dynamic-require
            const pu = require('../lib/parseRaidULDUAR');
            if (pu) {
              if (pu.parseTableUlduar) parseTableUldFn = pu.parseTableUlduar;
              else if (pu.default && pu.default.parseTableUlduar) parseTableUldFn = pu.default.parseTableUlduar;
            }
          } catch(_inner) { /* ignore */ }
        }
      }catch(_e){ /* fall back to inline DOM parsing below */ }

      // Prefer dedicated TOC parser when available, otherwise the generic one
      let effectiveParse = parseTableFn;
      if (raidKey === 'TOC' && typeof parseTableTocFn === 'function') effectiveParse = parseTableTocFn;
      else if (raidKey === 'ULDUAR' && typeof parseTableUldFn === 'function') effectiveParse = parseTableUldFn;
      if(effectiveParse && typeof effectiveParse === 'function'){
        const rows = effectiveParse(html);
        rows.forEach(r => {
          if(!r || !r.boss) return;
          // Find matching boss entry from bossesList using a relaxed normalization so
          // small wording differences (extra words, punctuation) don't prevent matches.
          const normalizeForMatch = (s) => (s||'').toString().toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
          const rb = normalizeForMatch(r.boss);
          const match = bossesList.find(b => {
            const nb = normalizeForMatch(b);
            return nb === rb || nb.indexOf(rb) !== -1 || rb.indexOf(nb) !== -1;
          });
          if(!match) return;
          // Map by raid-specific codes
          if(raidKey === 'TOC'){
            if(r.code === 'TOC 10NM') out[match]['10NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'TOC 10HC') out[match]['10HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'TOC 25NM') out[match]['25NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'TOC 25HC') out[match]['25HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
          } else if (raidKey === 'ULDUAR'){
            if(r.code === 'ULDUAR 10NM') out[match]['10NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ULDUAR 10HC') out[match]['10HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ULDUAR 25NM') out[match]['25NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ULDUAR 25HC') out[match]['25HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
          } else {
            if(r.code === 'ICC 10NM') out[match]['10NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ICC 10HC') out[match]['10HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ICC 25NM') out[match]['25NM'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
            else if(r.code === 'ICC 25HC') out[match]['25HC'] = (r.rawValue != null ? String(r.rawValue) : (r.value != null ? String(r.value) : '- -'));
          }
        });
        return out;
      }

      // Fallback: previous DOMParser-based approach if module import isn't available
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const rows = doc.querySelectorAll('tbody#data-table-list tr, table#data-table tr, table.dataTable tr');
      rows.forEach(tr => {
        try{
          const tds = tr.querySelectorAll('td');
          if(!tds || tds.length < 2) return;
          const desc = (tds[0].textContent || '').trim().replace(/\s+/g,' ');
          const val = (tds[1].textContent || '').trim();
          if(!desc) return;
          const low = desc.toLowerCase();
          // Some Warmane pages (notably TOC) use the phrase "Grand Crusader" for heroic
          // so treat that as heroic as well when parsing TOC. For other raids keep the
          // generic 'heroic' detection.
          const isHeroic = /\bheroic\b/i.test(desc) || (raidKey === 'TOC' && /grand crusader/i.test(desc));
          const is10 = /(?:\b10\b|10-player|10 player|\(10 player\)|\(10-player\))/i.test(desc);
          const is25 = /(?:\b25\b|25-player|25 player|\(25 player\)|\(25-player\))/i.test(desc);
          bossesList.forEach(boss => {
            const bl = boss.toLowerCase();
            // For TOC the final "Times completed the Trial of the Crusader" stat
            // may appear in the armory as either "Trial of the Crusader" or
            // "Trial of the Grand Crusader" depending on difficulty. Support both
            // variants when matching the description text.
            const alternateBossNames = [bl];
            if(raidKey === 'TOC' && /trial of the crusader/.test(bl)){
              alternateBossNames.push(bl.replace(/trial of the crusader/, 'trial of the grand crusader'));
            }
            const matchesBoss = alternateBossNames.some(n => low.indexOf(n) !== -1);
            if(!matchesBoss) return;
            if(is10){ if(isHeroic) out[boss]['10HC'] = val; else out[boss]['10NM'] = val; }
            if(is25){ if(isHeroic) out[boss]['25HC'] = val; else out[boss]['25NM'] = val; }
            if(!is10 && !is25){
              const parenMatch = desc.match(/\(([^)]+)\)/);
              if(parenMatch && /10\s*player/i.test(parenMatch[1])){ if(isHeroic) out[boss]['10HC'] = val; else out[boss]['10NM'] = val; }
              if(parenMatch && /25\s*player/i.test(parenMatch[1])){ if(isHeroic) out[boss]['25HC'] = val; else out[boss]['25NM'] = val; }
            }
          });
        }catch(_e){}
      });
    }catch(_e){}
    return out;
  }

  // Fetch raid statistics (lazy) for a given raid key using the statistics proxy
  async function fetchRaidStats(raidKey){
    // Basic guard: ensure we have a selected character
    if(!character || !character.name || !character.realm) {
      return;
    }
    // Avoid duplicate fetches
    if(raidLoading[raidKey]) {
      return;
    }
    setRaidLoading(prev => ({ ...prev, [raidKey]: true }));
    setRaidError(prev => ({ ...prev, [raidKey]: null }));
    try{
      // Production: POST multipart/form-data directly to the armory character statistics
      // so the nginx reverse-proxy can forward the request to the upstream site.
      // The dev-only `/armory/statistics` JSON endpoint is replaced by sending
      // a browser FormData payload to `/armory/character/:name/:realm/statistics`.
      const statsUrl = `/armory/character/${encodeURIComponent(character.name)}/${encodeURIComponent(character.realm)}/statistics`;
      const fd = new FormData();
      fd.append('category', String(RAID_CATEGORY[raidKey]));
      const resp = await fetch(statsUrl, {
        method: 'POST',
        // Let the browser set the Content-Type (multipart/form-data; boundary=...)
        body: fd
      });
      if(!resp.ok){
        const txt = await resp.text();
        throw new Error(`HTTP ${resp.status} ${txt}`);
      }
      const html = await resp.text();
  const parsed = parseRaidStatisticsHtml(html, RAID_BOSSES[raidKey] || [], raidKey);
      setRaidBossData(prev => ({ ...prev, [raidKey]: parsed }));
      // If this is TOC, also fetch the achievements category pages and prefetch any
      // achievement tooltips that correspond to the TOC bosses so hover/tooltips
      // are available quickly.
      if(raidKey === 'TOC'){
        try{
          // Achievement category IDs (as used elsewhere in the app)
          const TOC_CAT_10 = 15001; // TOC 10-player achievements
          const TOC_CAT_25 = 15002; // TOC 25-player achievements
          const fetchAchievementsCategory = async (categoryId) => {
            const controller2 = new AbortController();
            const timeout2 = setTimeout(()=>controller2.abort(), 15000);
            try{
              const url = `/armory/character/${encodeURIComponent(character.name)}/${encodeURIComponent(character.realm)}/achievements`;
              const body = new URLSearchParams({ category: String(categoryId) }).toString();
              const res = await fetch(url, { method: 'POST', signal: controller2.signal, headers: { 'Accept': '*/*', 'Content-Type': 'application/x-www-form-urlencoded' }, body });
              const txt = await res.text();
              if(/^{\s*"/.test(txt)){
                try{ const json = JSON.parse(txt); if(json && typeof json.content === 'string') return json.content; } catch(_e){}
              }
              return txt;
            } finally { clearTimeout(timeout2); }
          };

          const [html10, html25] = await Promise.all([
            fetchAchievementsCategory(TOC_CAT_10).catch(()=>''),
            fetchAchievementsCategory(TOC_CAT_25).catch(()=>''),
          ]);

          const combined = `${html10 || ''}\n${html25 || ''}`;
          if(combined){
            // Find achievement anchors and their text
            // Matches patterns like /achievement=1234 or href="/achievement=1234"
            const achRe = /(?:href=")?\/?achievement=([0-9]+)[^">]*["']?[^>]*>(.*?)<\/a>/gi;
            const simpleRe = /achievement=([0-9]+)/gi;
            const found = new Map(); // titleLower -> id
            let m;
            // First attempt: full anchor match
            while((m = achRe.exec(combined)) !== null){
              try{
                const id = parseInt(m[1],10);
                const title = (m[2] || '').replace(/<[^>]+>/g,'').trim();
                if(id && title){ found.set(title.toLowerCase(), id); }
              }catch(_e){}
            }
            // Fallback: find any achievement ids and try to capture nearby text
            if(found.size === 0){
              while((m = simpleRe.exec(combined)) !== null){
                const id = parseInt(m[1],10);
                if(!id) continue;
                // try to extract a surrounding 120 chars and strip tags
                const idx = m.index;
                const slice = combined.slice(Math.max(0, idx-120), idx+120);
                const txt = slice.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim();
                if(txt) found.set(txt.toLowerCase(), id);
              }
            }

            // For each TOC boss, try to find an achievement ID by matching boss text
            const bossNames = RAID_BOSSES['TOC'] || [];
            bossNames.forEach(boss => {
              try{
                const bl = (boss || '').toLowerCase();
                // Special-case the final 'Times completed the Trial of the Crusader' label
                const alternateBosses = [bl];
                if(/trial of the crusader/.test(bl)) alternateBosses.push(bl.replace(/trial of the crusader/g, 'trial of the grand crusader'));

                // Direct substring match against discovered achievement titles
                for(const [titleLower, id] of found.entries()){
                  const match = alternateBosses.some(n => (titleLower.indexOf(n) !== -1) || (n.indexOf(titleLower) !== -1));
                  if(match){
                    // Prefetch tooltip via existing queueFetchActivity
                    try{ queueFetchActivity('achievement', id); } catch(_e){}
                    break;
                  }
                }
              }catch(_e){}
            });
          }
        }catch(_e){ /* non-fatal - achievements prefetch failed */ }
      }
    }catch(e){
      setRaidError(prev => ({ ...prev, [raidKey]: (e && e.message) || 'error' }));
    }finally{
      setRaidLoading(prev => ({ ...prev, [raidKey]: false }));
    }
  }

  function handleToggleRaid(raidKey){
    setExpandedRaids(prev => {
      const willExpand = !prev[raidKey];
      const next = { ...prev, [raidKey]: willExpand };
      // If expanding and we don't have data yet, fetch it
      if(willExpand && !raidBossData[raidKey] && !raidLoading[raidKey]){
        // fire-and-forget; fetchRaidStats updates state when complete
        fetchRaidStats(raidKey);
      }
      return next;
    });
  }
  
  // Write tooltip content once per item; then measure and freeze size
  useEffect(()=>{
    if(tooltipFrameRef.current && hoverPos.visible){
      const iframe = tooltipFrameRef.current;
      const doc = iframe.contentDocument;
      if(doc){
        // Reset iframe size so previous item's size doesn't constrain measurement
        iframe.style.width = 'auto';
        iframe.style.height = 'auto';
        const raw = hoverPos.content || 'Loading...';
    // If we have an itemId and a gear score lookup, inject the Gear Score into the tooltip HTML
    let enriched = raw;
    try{
      const id = hoverPos.itemId;
      const gs = id && GEAR_SCORE_MAP ? GEAR_SCORE_MAP[id] : null;
      const dbItemLevel = (id && ITEM_ILVL_MAP && typeof ITEM_ILVL_MAP[id] === 'number') ? ITEM_ILVL_MAP[id] : null;
      
      // If we have a DB item level and the parsed/enriched tooltip doesn't already show it, insert it
      if(dbItemLevel){
        // If tooltip already contains "Item Level" text, skip
        if(!/Item Level\s*\d+/i.test(enriched)){
          // Try to insert after a durability line like "Durability 100 / 100" or similar
          const durabilityRegex = /(Durability[^<]*<[^>]*>|Durability[^<\n]*\d+\s*\/\s*\d+)/i;
          if(durabilityRegex.test(enriched)){
            enriched = enriched.replace(durabilityRegex, (m)=> `${m}<span>Item Level ${dbItemLevel}</span>`);
          }
        }
      }
      
      if(gs || gs === 0){
        // Insert a small blue line after the first occurrence of "Item Level <num>"
        // If not found, append after the first closing </th> of the header area.
        const ilRegex = /(Item Level\s*\d{1,4})/i;
        if(ilRegex.test(enriched)){
          // Put Gear Score on its own line, inherit item-level text styling but color it blue
          // Label stays white, numeric value is blue
          enriched = enriched.replace(ilRegex, (m)=> `${m}<br/><span style='color:#FFFFFF;'>Gear Score: <span style='color:#4AA3FF;'>${gs}</span></span>`);
        } else {
          // fallback: append a block-level line near the top of the tooltip inside the cavernoftime container
          enriched = enriched.replace(/(<div[^>]*class=['"]?cavernoftime-tt['"]?[^>]*>)/i, `$1<div style='color:#FFFFFF;margin-top:4px;'>Gear Score: <span style='color:#4AA3FF;'>${gs}</span></div>`);
        }
      }
    }catch(_e){}
    const wrapped = /cavernoftime-tt/.test(enriched) ? enriched : `<div class="cavernoftime-tt">${enriched}</div>`;
  doc.open();
  doc.write(`<html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:#0b0b0b;overflow:hidden}#tt-pad{display:inline-block;padding:0 1px 1px 0}</style><link rel="stylesheet" href="${COT_CSS_PATH}"></head><body><div id="tt-pad">${wrapped}</div></body></html>`);
        doc.close();
        // Post-process tooltip HTML to highlight transmogrified lines in Paladin color
        try {
          // Small timeout to ensure DOM is ready inside iframe
          setTimeout(() => {
            try {
              const bodyDoc = doc.body;
              if (!bodyDoc) return;
              const searchText = /transmogrified to|transmog to|transmogrified/i;
              // Find any elements containing the keyword
              const walker = doc.createTreeWalker(bodyDoc, NodeFilter.SHOW_TEXT, null);
              let node;
              const palColor = '#FFD700';
              while ((node = walker.nextNode())) {
                try {
                  const txt = (node.nodeValue || '').trim();
                  if (!txt) continue;
                  if (searchText.test(txt)) {
                    // Prefer the closest parent <span> or <td> or <div> to color
                    let parent = node.parentElement;
                    // If the parent is a table cell, try to color a child <span> with the item name
                    const candidate = (el) => {
                      if (!el) return null;
                      // look for a span inside
                      const span = el.querySelector && el.querySelector('span');
                      if (span) return span;
                      // else fallback to the element itself
                      return el;
                    };
                    const target = candidate(parent) || parent;
                    if (target && target.style) {
                      target.style.color = palColor;
                      // make it a bit more visible
                      target.style.fontWeight = '700';
                    }
                    // Also color immediate previous sibling if it looks like an item name
                    try {
                      const prev = parent && parent.previousElementSibling;
                      if (prev && prev.style) {
                        prev.style.color = palColor;
                        prev.style.fontWeight = '700';
                      }
                    } catch (_) {}
                  }
                } catch(_) {}
              }
            } catch (_) {}
          }, 8);
        } catch (_) {}
        // After render, measure ONCE and lock dimensions
        setTimeout(()=>{
          try {
            const body = doc.body;
            if(body){
              // Hide internal scrollbars
              body.style.overflow = 'hidden';
              doc.documentElement && (doc.documentElement.style.overflow = 'hidden');
              // Measure precise wrapper size
              const wrapper = doc.querySelector('.cavernoftime-tt') || body;
              const rect = wrapper.getBoundingClientRect();
              const pad = 8;
              const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
              const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
              // Add a tiny fudge so borders/shadows do not clip
              const naturalW = Math.ceil(rect.width) + 2;
              const naturalH = Math.ceil(rect.height) + 2;
              const minH = 80;
              const finalW = Math.min(vw - 2*pad, naturalW);
              const finalH = Math.min(vh - 2*pad, Math.max(minH, naturalH));
              iframe.style.overflow = 'hidden';
              iframe.style.height = finalH + 'px';
              iframe.style.width = finalW + 'px';
              // Freeze size in state so future mouse moves don't change it
              setHoverPos(h=>{
                if(!h.visible) return h;
                const pos = clampToViewport(h.x, h.y, finalW, finalH);
                return { ...h, width: finalW, height: finalH, x: pos.x, y: pos.y };
              });
              // Second pass: if content still overflows, expand once more
              setTimeout(()=>{
                try{
                  const needW = Math.ceil(body.scrollWidth) + 2;
                  const needH = Math.ceil(body.scrollHeight) + 2;
                  if(needW > finalW || needH > finalH){
                    const adjW = Math.min(vw - 2*pad, Math.max(finalW, needW));
                    const adjH = Math.min(vh - 2*pad, Math.max(finalH, needH));
                    iframe.style.width = adjW + 'px';
                    iframe.style.height = adjH + 'px';
                    setHoverPos(h=>{
                      if(!h.visible) return h;
                      const pos2 = clampToViewport(h.x, h.y, adjW, adjH);
                      return { ...h, width: adjW, height: adjH, x: pos2.x, y: pos2.y };
                    });
                  }
                }catch(_e){}
              }, 30);
            }
          } catch(e){ if(DEBUG) console.debug('Resize failed', e); }
        }, 30);
      }
    }
  },[hoverPos.itemId, hoverPos.visible, hoverPos.content, DEBUG]);

  const renderEquipmentSlot = (slotType) => {
    const eq = findEquipment(character, slotType);
    const className = `equipment-slot ${slotClassMap[slotType]}`;
    const hasItem = !!(eq && eq.item);
    const detail = hasItem && itemDetails[eq?.item];

    // Build icon URL: prefer local items DB icon (ITEM_ICON_MAP), fallback to parsed CoT detail
    const itemId = eq?.item;
    const dbIcon = typeof itemId !== 'undefined' ? ITEM_ICON_MAP[itemId] : undefined;
    const iconUrl = hasItem && (dbIcon || detail)
      ? (dbIcon || DEFAULT_ICON_URL.replace(/[^/]+$/, `${(detail.icon || 'inv_misc_questionmark').toLowerCase()}.jpg`))
      : null;
    
    // Prefer DB quality (+1 already applied in QUALITY_MAP). Fallback to CoT parsed quality + 1 to align with palette.
    const qualityIndex = (typeof itemId !== 'undefined' && QUALITY_MAP[itemId] !== undefined)
      ? QUALITY_MAP[itemId]
      : (typeof (detail && detail.quality) === 'number' ? (detail.quality + 1) : undefined);
    const qColor = getQualityBorderColor(qualityIndex);
    return (
      <div
        key={slotType}
        className={className}
        title={slotType}
        onMouseEnter={(e)=>handleItemEnter(e, eq)}
        onMouseMove={handleItemMove}
        onMouseLeave={handleItemLeave}
        onClick={()=>{ if(eq) window.open(`https://wotlk.cavernoftime.com/item=${eq.item}`, '_blank','noopener,noreferrer'); }}
        style={{
          // If slot has an item, use its icon; otherwise use the equipment sprite and crop a 50x50 region
          backgroundImage: hasItem && iconUrl ? `url(${iconUrl})` : `url(${EQUIP_SPRITE})`,
          backgroundSize: hasItem && iconUrl ? 'cover' : undefined,
          // For sprite, compute backgroundPosition so the 50x50 area is shown
          backgroundPosition: hasItem && iconUrl ? 'center' : (slotSpritePositions[slotClassMap[slotType]] ? `-${slotSpritePositions[slotClassMap[slotType]].x}px -${slotSpritePositions[slotClassMap[slotType]].y}px` : 'center'),
          backgroundRepeat: hasItem && iconUrl ? 'no-repeat' : 'no-repeat',
          // Ensure crisp 50x50 section is shown (handled above)
          borderColor: qColor
        }}
      >
        {/* Prefer item level from the local DB to avoid showing a loading spinner */}
        {hasItem && ITEM_ILVL_MAP[itemId] && (
          <div style={{position:'absolute',bottom:1,right:2,fontSize:10,fontWeight:'700',color:'#FFFFFF',textShadow:'1px 1px 2px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.5)'}}>{ITEM_ILVL_MAP[itemId]}</div>
        )}
        {hasItem && !ITEM_ILVL_MAP[itemId] && detail && detail.itemLevel && detail.status === 'ok' && (
          <div style={{position:'absolute',bottom:1,right:2,fontSize:10,fontWeight:'700',color:'#FFFFFF',textShadow:'1px 1px 2px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.5)'}}>{detail.itemLevel}</div>
        )}
        {hasItem && !ITEM_ILVL_MAP[itemId] && (!detail || detail.status === 'loading') && <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:'#ccc',background:'rgba(0,0,0,0.25)'}}>⏳</div>}
        {hasItem && detail && detail.status === 'error' && (
          <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'rgba(64,0,0,0.55)',color:'#ff5555',fontSize:10,fontWeight:700}}>
            !
            <button onClick={(ev)=>manualRetry(eq.item, ev, { ench: eq.ench ? String(eq.ench) : undefined, gems: Array.isArray(eq.gems) ? eq.gems.map(g=>g||0).join(':') : undefined, transmog: eq.transmog ? String(eq.transmog) : undefined })} style={{marginTop:2,padding:'0 2px',fontSize:8,cursor:'pointer',background:'#220',color:'#ffb3b3',border:'1px solid #553',borderRadius:2}}>Retry</button>
          </div>
        )}
      </div>
    );
  };

  // Build structured stats sections from Warmane Armory parsed columns
  const buildStatsSections = useCallback((armoryStats)=>{
    const sections = { base: [], melee: [], ranged: [], spell: [], defenses: [], resistances: [] };
    if(!armoryStats || !Array.isArray(armoryStats)) return sections;
    const allLines = [];
    armoryStats.forEach(col => {
      if(Array.isArray(col)) allLines.push(...col);
    });
    let current = null;
    const pushLine = (arr, line) => {
      const idx = line.indexOf(':');
      if(idx > 0){
        const label = line.slice(0, idx).trim();
        const value = line.slice(idx+1).trim();
        arr.push({ label, value });
      }
    };
    allLines.forEach(ln => {
      const t = (ln||'').trim();
      if(!t) return;
      // Header lines are plain words without colon; also handle common Warmane headings
      const noColon = t.indexOf(':') === -1;
      const header = noColon ? t.trim() : null;
      if(header){
        const norm = header.toLowerCase();
        if(norm === 'attributes' || norm === 'base stats' || norm === 'stats' || norm === 'base') current = 'attributes';
        else if(norm === 'melee') current = 'melee';
        else if(norm === 'ranged' || norm === 'range') current = 'ranged';
        else if(norm === 'spell' || norm === 'spells' || norm === 'magic') current = 'spell';
        else if(norm === 'defense' || norm === 'defences' || norm === 'defenses' || norm === 'defence') current = 'defense';
        else if(norm === 'resistances' || norm === 'resistance') current = 'resistances';
        else current = null;
        return;
      }
      if(!current) return;
      if(current === 'attributes') pushLine(sections.base, t);
      else if(current === 'melee') pushLine(sections.melee, t);
      else if(current === 'ranged') pushLine(sections.ranged, t);
      else if(current === 'spell') pushLine(sections.spell, t);
      else if(current === 'defense') pushLine(sections.defenses, t);
      else if(current === 'resistances') pushLine(sections.resistances, t);
    });
  // Ensure Armor appears in Base Stats if present under defenses
    const armorFromDefense = sections.defenses.find(x=>/^Armor$/i.test(x.label));
    const hasArmorInBase = sections.base.find(x=>/^Armor$/i.test(x.label));
    if(armorFromDefense && !hasArmorInBase){ sections.base.push(armorFromDefense); }

    // Move Expertise from Base Stats to Melee
    const isExpertise = (x)=> /^Expertise(?:\s+rating)?$/i.test(x.label || '');
    const expertiseRows = sections.base.filter(isExpertise);
    if(expertiseRows.length){
      sections.base = sections.base.filter(x=>!isExpertise(x));
      const meleeLabels = new Set(sections.melee.map(m=> (m.label || '').toLowerCase()));
      expertiseRows.forEach(row=>{
        if(!meleeLabels.has((row.label||'').toLowerCase())){
          sections.melee.push(row);
        }
      });
    }

    // Reorder Base Stats: Strength, Agility, Stamina, Intellect, Spirit, Armor
    const BASE_ORDER = ['Strength','Agility','Stamina','Intellect','Spirit','Armor'];
    const norm = (s)=> (s||'').trim().toLowerCase();
    const wanted = BASE_ORDER.map(x=>x.toLowerCase());
    const ordered = [];
    const used = new Set();
    // Build order-first entries
    for(const label of wanted){
      const row = sections.base.find(r => norm(r.label) === label);
      if(row){ ordered.push(row); used.add(norm(row.label)); }
    }
    // Append remaining base stats in original order, skipping any already placed
    const remaining = sections.base.filter(r => !used.has(norm(r.label)));
    sections.base = [...ordered, ...remaining];

    // Normalize melee label variants: 'critical', 'critical strike', 'crit' -> 'Crit Chance'
    sections.melee = sections.melee.map(r => {
      const nlab = norm(r.label);
      if(nlab === 'critical' || nlab === 'critical chance' || nlab === 'critical strike' || nlab === 'critical strike chance' || nlab === 'crit' || nlab === 'crit chance'){
        return { ...r, label: 'Crit Chance' };
      }
      return r;
    });

    // Reorder Melee Stats: Damage, Speed, Power, Hit Rating, Crit Chance, Expertise
    const MELEE_ORDER = ['damage','speed','power','hit rating','crit chance','expertise'];
    const keyFor = (label) => {
      const n = norm(label);
      if(n === 'attack power') return 'power';
      if(n === 'critical chance' || n === 'crit') return 'crit chance';
      if(n === 'melee hit rating') return 'hit rating';
      if(/^expertise(?:\s+rating)?$/.test(n)) return 'expertise';
      return n;
    };
    const meleeUsed = new Set();
    const meleeOrdered = [];
    for(const want of MELEE_ORDER){
      const row = sections.melee.find(r => keyFor(r.label) === want);
      if(row){ meleeOrdered.push(row); meleeUsed.add(row); }
    }
    const meleeRemaining = sections.melee.filter(r => !meleeUsed.has(r));
    sections.melee = [...meleeOrdered, ...meleeRemaining];

    // Normalize ranged label variants: 'critical', 'critical strike', 'crit' -> 'Crit Chance'
    sections.ranged = sections.ranged.map(r => {
      const nlab = norm(r.label);
      if(nlab === 'critical' || nlab === 'critical chance' || nlab === 'critical strike' || nlab === 'critical strike chance' || nlab === 'crit' || nlab === 'crit chance' || nlab === 'ranged crit' || nlab === 'ranged crit chance'){
        return { ...r, label: 'Crit Chance' };
      }
      return r;
    });

    // Reorder Ranged Stats: Damage, Speed, Power, Hit Rating, Crit Chance
    const RANGED_ORDER = ['damage','speed','power','hit rating','crit chance'];
    const keyForR = (label) => {
      const n = norm(label);
      if(n === 'attack power' || n === 'ranged attack power') return 'power';
      if(n === 'critical chance' || n === 'crit' || n === 'ranged crit' || n === 'ranged crit chance') return 'crit chance';
      if(n === 'ranged hit rating') return 'hit rating';
      return n;
    };
    const rangedUsed = new Set();
    const rangedOrdered = [];
    for(const want of RANGED_ORDER){
      const row = sections.ranged.find(r => keyForR(r.label) === want);
      if(row){ rangedOrdered.push(row); rangedUsed.add(row); }
    }
    const rangedRemaining = sections.ranged.filter(r => !rangedUsed.has(r));
    sections.ranged = [...rangedOrdered, ...rangedRemaining];

    // Normalize spell label variants and rename per request
    sections.spell = sections.spell.map(r => {
      const nlab = norm(r.label);
      if(nlab === 'power' || nlab === 'spell power' || nlab === 'spellpower' || nlab === 'bonus damage' || nlab === 'bonus spell damage'){
        return { ...r, label: 'Bonus Damage' };
      }
      if(nlab === 'healing' || nlab === 'bonus healing' || nlab === 'spell healing'){
        return { ...r, label: 'Bonus Healing' };
      }
      if(nlab === 'critical' || nlab === 'critical chance' || nlab === 'critical strike' || nlab === 'critical strike chance' || nlab === 'crit' || nlab === 'crit chance'){
        return { ...r, label: 'Crit Chance' };
      }
      if(nlab === 'haste' || nlab === 'haste rating' || nlab === 'spell haste rating' || nlab === 'spell haste'){
        return { ...r, label: 'Haste Rating' };
      }
      if(nlab === 'spell hit rating' || nlab === 'hit rating' || nlab === 'spell hit'){
        return { ...r, label: 'Hit Rating' };
      }
      return r;
    });

    // Reorder Spell Stats: Bonus Damage, Bonus Healing, Hit Rating, Crit Chance, Haste Rating
    const SPELL_ORDER = ['bonus damage','bonus healing','hit rating','crit chance','haste rating'];
    const keyForS = (label) => {
      const n = norm(label);
      if(n === 'power' || n === 'spell power' || n === 'bonus damage' || n === 'bonus spell damage') return 'bonus damage';
      if(n === 'healing' || n === 'bonus healing' || n === 'spell healing') return 'bonus healing';
      if(n === 'spell hit rating' || n === 'hit rating' || n === 'spell hit') return 'hit rating';
      if(n === 'critical chance' || n === 'critical' || n === 'critical strike' || n === 'critical strike chance' || n === 'crit' || n === 'crit chance') return 'crit chance';
      if(n === 'haste' || n === 'haste rating' || n === 'spell haste' || n === 'spell haste rating') return 'haste rating';
      return n;
    };
    const spellUsed = new Set();
    const spellOrdered = [];
    for(const want of SPELL_ORDER){
      const row = sections.spell.find(r => keyForS(r.label) === want);
      if(row){ spellOrdered.push(row); spellUsed.add(row); }
    }
    const spellRemaining = sections.spell.filter(r => !spellUsed.has(r));
    sections.spell = [...spellOrdered, ...spellRemaining];
    return sections;
  },[]);

  const STAT_OPTIONS = ['Base Stats','Melee','Ranged','Spell','Defenses','Resistances'];
  const sections = buildStatsSections(character?.armoryStats);
  const statsByType = (type)=>{
    const k = (type||'').toLowerCase();
    switch(k){
      case 'base stats':
      case 'base':
      case 'attributes':
        return sections.base;
      case 'melee': return sections.melee;
      case 'ranged':
      case 'range':
        return sections.ranged;
      case 'spell':
      case 'spells':
      case 'magic':
        return sections.spell;
      case 'defenses':
      case 'defense':
      case 'defence':
      case 'defences':
        return sections.defenses;
      case 'resistances':
      case 'resistance':
        return sections.resistances;
      default: return [];
    }
  };

  // When loading, hide the entire panel so only the global overlay spinner
  // (QueueBar) is visible and there are no duplicate UI elements.
  if (loading) return null;
  if (error) return <div className="character-panel error">{error}</div>;
  // If no character is selected, render nothing instead of a placeholder label
  if (!character) return null;

  // Helper functions for asset paths
  const getSpecIcon = (className, specName) => {
    if (!className || !specName) return require('../assets/specs/warrior_arms.jpg');
    // Normalize class and spec names to match file naming
    const classKey = className.toLowerCase().replace(' ', '_');
    const specKey = specName.toLowerCase().replace(/ /g, '_');
    // Special case for death knight
    let fileName = '';
    if (classKey === 'death_knight') {
      fileName = `death_knight_${specKey}.jpg`;
    } else {
      fileName = `${classKey}_${specKey}.jpg`;
    }
    try {
      return require(`../assets/specs/${fileName}`);
    } catch (e) {
      return require('../assets/specs/warrior_arms.jpg');
    }
  };

  const getRaceIcon = (race, gender) => {
    const raceMap = {
      'Human': 'human',
      'Dwarf': 'dwarf',
      'Night Elf': 'night_elf',
      'Gnome': 'gnome',
      'Draenei': 'draenei',
      'Blood Elf': 'blood_elf',
      'Orc': 'orc',
      'Undead': 'undead',
      'Tauren': 'tauren',
      'Troll': 'troll'
    };
    
    const genderStr = getGenderString(gender);
    const fileName = `${raceMap[race] || 'human'}_${genderStr}.gif`;
    try {
      return require(`../assets/races/${fileName}`);
    } catch (e) {
      return require('../assets/races/human_male.gif');
    }
  };

  const getProfessionIcon = (professionName) => {
    const profMap = {
      'Alchemy': 'alchemy',
      'Blacksmithing': 'blacksmithing',
      'Enchanting': 'enchanting',
      'Engineering': 'engineering',
      'Herbalism': 'herbalism',
      'Inscription': 'inscription',
      'Jewelcrafting': 'jewelcrafting',
      'Leatherworking': 'leatherworking',
      'Mining': 'mining',
      'Skinning': 'skinning',
      'Tailoring': 'tailoring',

      // Secondary professions
      'Cooking': 'cooking',
      'Fishing': 'fishing',
      'First Aid': 'first_aid'
    };
    const fileName = `${profMap[professionName] || 'mining'}.jpg`;
    try {
      return require(`../assets/professions/${fileName}`);
    } catch (e) {
      return require('../assets/professions/mining.jpg');
    }
  };

  // Helper function to determine gender string
  const getGenderString = (gender) => {
    if (gender === 1 || gender === '1' || gender === 'Female' || gender === 'female' || gender === 2) {
      return 'female';
    }
    return 'male'; // default fallback
  };

  return (
    <div className="character-panel">
      {/* Header Section */}
      <div className="character-header">
        <div className="character-title-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
          <div className="character-header-left">
            <div className="faction-header">
              {(() => {
                const src = character.faction && character.faction.includes('Horde') ? HORDE_ICON : ALLIANCE_ICON;
                return (
                  <img src={src} alt={character.faction} className="faction-badge-lg" />
                );
              })()}
              <div className="name-and-guild">
            <h1 className="character-name" style={{cursor:'pointer', margin: 0}}
              onClick={() => window.open(`https://armory.warmane.com/character/${encodeURIComponent(character.name)}/${encodeURIComponent(character.realm)}/summary`, '_blank', 'noopener,noreferrer')}
              title="Open in Warmane Armory"
            >
              {character.name}
              <span
                className="online-dot-inline"
                title={character.online ? 'Online' : 'Offline'}
                style={{
                  background: character.online ? '#00e676' : '#a12a2a',
                  boxShadow: character.online ? '0 0 8px rgba(0,230,118,0.6)' : 'none',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              />
            </h1>
            {character.guild ? (
              <div className="character-subtitle">
                <span className="character-guild" style={{cursor:'pointer'}}
                  onClick={() => window.open(`https://armory.warmane.com/guild/${encodeURIComponent(character.guild)}/${encodeURIComponent(character.realm)}/summary/${encodeURIComponent(character.name)}`,'_blank','noopener,noreferrer')}
                  title="Open in Warmane Armory"
                >
                  {character.guild}
                  {guildRank ? (
                    <span style={{ marginLeft: 8, fontSize: 12 }} title="Guild rank">
                      ({guildRank})
                    </span>
                  ) : null}
                </span>
                {character.realm && (
                  <div className="character-subtitle">
                    <span>
                      {character.realm}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="character-header-stats-row">
          <div className="header-stats-grid" style={{marginRight:12}}>
            <div className="header-stat-card" style={cardHeight ? {height: cardHeight} : undefined}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="status-icon" style={{overflow:'hidden',borderRadius:'50%'}}>
                  <img src={require('../assets/gear score.jpg')} alt="gear" style={{width:'100%',height:'100%',objectFit:'cover',display:'block',borderRadius:'50%'}} />
                </div>
                <div style={{display:'flex',flexDirection:'column'}}>
                  <div className="stat-main">{character.gearScore || 0}</div>
                  <div className="stat-label">Gear score</div>
                </div>
              </div>
            </div>

            <div className="header-stat-card" style={cardHeight ? {height: cardHeight} : undefined}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="status-icon" style={{overflow:'hidden',borderRadius:'50%'}}>
                  <img src={require('../assets/achievements.jpg')} alt="ach" style={{width:'100%',height:'100%',objectFit:'cover',display:'block',borderRadius:'50%'}} />
                </div>
                <div style={{display:'flex',flexDirection:'column'}}>
                  <div className="stat-main">{character.achievementpoints || 0}</div>
                  <div className="stat-label">Achievement points</div>
                </div>
              </div>
            </div>

            <div className="header-stat-card" style={cardHeight ? {height: cardHeight} : undefined} title={Array.isArray(character?.missingGems) && character.missingGems.length ? `Missing gems: ${character.missingGems.join(', ')}` : 'All items gemmed'} onMouseEnter={(e)=>handleStatusEnter(e,'gems')} onMouseLeave={handleStatusLeave}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="status-icon" style={{background: (Array.isArray(character?.missingGems) && character.missingGems.length) ? 'rgba(183,28,28,0.18)' : 'rgba(0,200,83,0.12)', borderColor: (Array.isArray(character?.missingGems) && character.missingGems.length) ? '#B71C1C44' : '#00C85344'}}>
                  <span style={{color:'#FFD700', fontWeight:800}}>{(Array.isArray(character?.missingGems) && character.missingGems.length) ? '✖' : '✔'}</span>
                </div>
                <div style={{display:'flex',flexDirection:'column'}}>
                  <div className="stat-main">Gems</div>
                  <div className="stat-label">{(Array.isArray(character?.missingGems) && character.missingGems.length) ? `${character.missingGems.length} missing` : 'All gemmed'}</div>
                </div>
              </div>
            </div>

            <div className="header-stat-card" style={cardHeight ? {height: cardHeight} : undefined} title={Array.isArray(character?.missingEnchants) && character.missingEnchants.length ? `Missing enchants: ${character.missingEnchants.join(', ')}` : 'All items enchanted'} onMouseEnter={(e)=>handleStatusEnter(e,'enchants')} onMouseLeave={handleStatusLeave}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div className="status-icon" style={{background: (Array.isArray(character?.missingEnchants) && character.missingEnchants.length) ? 'rgba(183,28,28,0.18)' : 'rgba(0,200,83,0.12)', borderColor: (Array.isArray(character?.missingEnchants) && character.missingEnchants.length) ? '#B71C1C44' : '#00C85344'}}>
                  <span style={{color:'#FFD700', fontWeight:800}}>{(Array.isArray(character?.missingEnchants) && character.missingEnchants.length) ? '✖' : '✔'}</span>
                </div>
                <div style={{display:'flex',flexDirection:'column'}}>
                  <div className="stat-main">Enchants</div>
                  <div className="stat-label">{(Array.isArray(character?.missingEnchants) && character.missingEnchants.length) ? `${character.missingEnchants.length} missing` : 'All enchanted'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="character-content">
        {/* Left Panel - Character Model and Equipment */}
        <div className="character-left-panel">
          <div className="info-section">
            <div className="character-model-container">
            <h3 className="section-title">Character</h3>
            <div className="character-icons-row">
              {/* Level badge - circular, same size and border as icons */}
              <div title='Level' style={{ color: '#FFD700', width: 40, height: 40, borderRadius: '50%', border: '2px solid #FFD700', background: 'rgba(255, 215, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                {character.level}
              </div>
              <img
                src={getRaceIcon(character.race, character.gender)}
                alt={`${character.race} icon`}
                title='Race'
                style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid #FFD700', background: '#222' }}
              />
              <img
                src={(() => {
                  try {
                    const className = character.class ? character.class.toLowerCase().replace(' ', '_') : 'warrior';
                    return require(`../assets/classes/${className}.gif`);
                  } catch (e) {
                    return require('../assets/classes/warrior.gif');
                  }
                })()}
                alt={`${character.class} icon`}
                title='Class'
                style={{ width: 40, height: 40, borderRadius: '50%', background: '#222', border: '2px solid #FFD700' }}
              />
              {/* Spec icon (derived from parsed talents or character data) */}
              {(() => {
                const specName = character?.spec;
                if(!specName) return null;
                try {
                  const specImg = getSpecIcon(character.class, specName);
                  return <img src={specImg} alt={`${specName} spec`} title={specName} style={{ width: 40, height: 40, borderRadius: '50%', background: '#222', border: '2px solid #FFD700' }} />;
                } catch(_e){ return null; }
              })()}
            </div>
            {/* Character Model Placeholder */}

            {/* Equipment Slots */}
            <div className="equipment-slots" ref={equipmentSlotsRef}>
              {classBgSrc && <img src={classBgSrc} className="class-bg" alt="Class background" style={classBgStyle || undefined} />}
              {/* Full-frame background model viewer behind slots */}
              <div className="equipment-left" ref={equipmentLeftRef}>
                {/* Head, Shoulders, Chest, etc. */}
                {['Head','Neck','Shoulders','Back','Chest','Shirt','Tabard','Wrist'].map(slot => renderEquipmentSlot(slot))}
              </div>
              <div className="equipment-middle">
                <div className="equipment-background-viewer">
                  {/* Important: The 3D viewer sits behind slots. Hovering slots updates tooltip state frequently.
                      Ensure the viewer component does not re-initialize on those rerenders.
                      See ModelViewer: options are kept in a ref and effect deps exclude unstable objects. */}
                  <ModelViewer
                    fill
                    character={character}
                    mode="3d"
                    view="character"
                  />
                </div>
                {/* 3 hand/relic slots render above the background viewer */}
                {['Main hand','Off hand','Relic'].map(slot => renderEquipmentSlot(slot))}
              </div>
              <div className="equipment-right" ref={equipmentRightRef}>
                {/* Neck, Weapon, Shield, etc. */}
                {['Hand','Waist','Legs','Feet','Ring 1','Ring 2','Trinket 1','Trinket 2'].map(slot => renderEquipmentSlot(slot))}
              </div>
            </div>
            {hoverPos.visible && (
              <iframe
                ref={tooltipFrameRef}
                title="item-tooltip"
                style={{position:'fixed', top:hoverPos.y, left:hoverPos.x, width:hoverPos.width, height:hoverPos.height, border:'none', background:'#111', zIndex:9999, pointerEvents:'none'}}
              />
            )}

            {/* Missing items popover for status icons */}
            {statusHover.visible && (
              <div
                ref={statusPopoverRef}
                style={{
                  position:'fixed',
                  top: statusHover.y,
                  left: statusHover.x,
                  zIndex: 10000,
                  background: 'rgba(19,19,19,0.96)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 6,
                  padding: '8px 10px',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
                  color:'#FFD700',
                  fontSize: 12,
                  maxWidth: 260,
                }}
                onMouseLeave={handleStatusLeave}
              >
                <div style={{fontWeight:700, marginBottom:6}}>
                  {statusHover.type === 'gems' ? 'Missing Gems' : 'Missing Enchants'}
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                  {statusHover.items.map((it, idx) => (
                    <div
                      key={idx}
                      style={{whiteSpace:'nowrap',padding:'2px 4px',borderRadius:4}}
                    >
                      - {it}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </div>

            <h3 className="section-title" style={{marginTop: 8}}>Stats</h3>
            {/* Character Stats moved here with two columns and dropdowns */}
            <div className="character-stats">
              <div className="stats-controls">
                <div className="stats-select">
                  <select value={leftStatsType} onChange={(e)=>setLeftStatsType(e.target.value)}>
                    {STAT_OPTIONS.map(opt=> <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="stats-select">
                  <select value={rightStatsType} onChange={(e)=>setRightStatsType(e.target.value)}>
                    {STAT_OPTIONS.map(opt=> <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>
              </div>
              <div className="stats-cols">
                <div className="stats-col-list">
                  {(statsByType(leftStatsType) || []).map((row, idx)=> (
                    <div className="stat-line" key={idx}>
                      <span className="stat-label">{row.label}:</span>
                      <span className="stat-value">{row.value}</span>
                    </div>
                  ))}
                  {Array.isArray(statsByType(leftStatsType)) && statsByType(leftStatsType).length === 0 && (sections.base.length + sections.melee.length + sections.ranged.length + sections.spell.length + sections.defenses.length) > 0 && (
                    <div className="stat-line"><span className="stat-value">No data for this category</span></div>
                  )}
                  {(!sections.base.length && !sections.melee.length && !sections.ranged.length && !sections.spell.length && !sections.defenses.length) && (
                    <div className="stat-line"><span className="stat-value">Stats unavailable</span></div>
                  )}
                </div>
                <div className="stats-col-list">
                  {(statsByType(rightStatsType) || []).map((row, idx)=> (
                    <div className="stat-line" key={idx}>
                      <span className="stat-label">{row.label}:</span>
                      <span className="stat-value">{row.value}</span>
                    </div>
                  ))}
                  {Array.isArray(statsByType(rightStatsType)) && statsByType(rightStatsType).length === 0 && (sections.base.length + sections.melee.length + sections.ranged.length + sections.spell.length + sections.defenses.length) > 0 && (
                    <div className="stat-line"><span className="stat-value">No data for this category</span></div>
                  )}
                </div>
              </div>
            </div>
          </div >
          <div className="info-section">
            <h3 className="section-title">Raid statistics</h3>            
            {/* Raid completion table moved into the left panel (below Stats) */}
            {character.raidProgress && (
              <div className="raid-progress">
                <div className="raid-grid raid-grid-head">
                  <div className="raid-col-head">Instance</div>
                  <div className="raid-col-head">10NM</div>
                  <div className="raid-col-head">10HC</div>
                  <div className="raid-col-head">25NM</div>
                  <div className="raid-col-head">25HC</div>
                </div>
                {['ICC','RS','TOC','ULDUAR'].map(r => (
                  <React.Fragment key={r}>
                    <div className="raid-grid raid-grid-row">
                      <div className="raid-name" style={{display:'flex',alignItems:'center',gap:8}}>
                        <button
                          onClick={() => handleToggleRaid(r)}
                          aria-expanded={!!expandedRaids[r]}
                          title={expandedRaids[r] ? 'Collapse' : 'Expand'}
                          style={{border:'none',background:'transparent',color:'#ccc',cursor:'pointer',padding:0,marginRight:6,fontSize:14,lineHeight:1}}
                        >{expandedRaids[r] ? '▾' : '▸'}</button>
                        <span style={{flex:'0 0 auto'}}>{r}</span>
                      </div>
                      {['10NM','10HC','25NM','25HC'].map(col => (
                        <div key={col} className="raid-cell">
                          {character.raidProgress?.[r]?.[col] ? (
                            <span className="tick" aria-label="Completed">✔</span>
                          ) : (
                            <span className="cross" aria-label="Not completed">✖</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Boss rows (lazy-loaded) */}
                    {expandedRaids[r] && (
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        {raidLoading[r] && (
                          <div className="raid-grid raid-grid-row" style={{opacity:0.9}}>
                            <div className="raid-name">Loading boss statistics...</div>
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                          </div>
                        )}
                        {raidError[r] && !raidLoading[r] && (
                          <div className="raid-grid raid-grid-row" style={{color:'#ffb3b3'}}>
                            <div className="raid-name">Failed to load boss stats: {raidError[r]}</div>
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                            <div className="raid-cell" />
                          </div>
                        )}
                        {!raidLoading[r] && !raidError[r] && (RAID_BOSSES[r] || []).map(boss => {
                          // Treat missing/no-kill entries uniformly as '-' (single dash)
                          const rawVals = (raidBossData[r] && raidBossData[r][boss]) || { '10NM':'-', '10HC':'-', '25NM':'-', '25HC':'-' };
                          const cols = ['10NM','10HC','25NM','25HC'];
                          const normalize = (v) => {
                            const s = (v || '').toString().trim();
                            if (s === '' || s === '-' || s === '- -' || /^0+$/.test(s) || s.toLowerCase() === 'n/a') return '-';
                            return s;
                          };
                          // Build normalized values
                          const vals = {};
                          cols.forEach(c => { vals[c] = normalize(rawVals[c]); });

                          const displayVals = {};
                          if (/halion/i.test(boss)) {
                            // For Halion: show '>=1' for each difficulty that has a recorded kill; otherwise '-'.
                            // Fallback: if Warmane omits the per-boss counts but the overall instance-level
                            // completion (character.raidProgress) reports the difficulty as completed, treat it as killed.
                            cols.forEach(c => {
                              if (vals[c] !== '-') {
                                displayVals[c] = '>=1';
                              } else if (character?.raidProgress && character.raidProgress[r] && character.raidProgress[r][c]) {
                                displayVals[c] = '>=1';
                              } else {
                                displayVals[c] = '-';
                              }
                            });
                          } else {
                            // For other bosses: show the numeric/raw value if present, otherwise '-'
                            cols.forEach(c => { displayVals[c] = (vals[c] !== '-') ? vals[c] : '-'; });
                          }
                          return (
                            <div key={`${r}-${boss}`} className="raid-grid raid-grid-row" style={{opacity:0.95}}>
                              <div className="raid-name" style={{fontSize:13, paddingLeft:18}}>{boss}</div>
                              <div
                                className="raid-cell"
                                title={/halion/i.test(boss) && displayVals['10NM'] === '>=1' ? "Killed at least once, but Warmane doesn't display it" : undefined}
                                aria-label={displayVals['10NM'] === '>=1' ? "Killed at least once" : undefined}
                              >{displayVals['10NM'] || '- -'}</div>
                              <div
                                className="raid-cell"
                                title={/halion/i.test(boss) && displayVals['10HC'] === '>=1' ? "Killed at least once, but Warmane doesn't display it" : undefined}
                                aria-label={displayVals['10HC'] === '>=1' ? "Killed at least once" : undefined}
                              >{displayVals['10HC'] || '- -'}</div>
                              <div
                                className="raid-cell"
                                title={/halion/i.test(boss) && displayVals['25NM'] === '>=1' ? "Killed at least once, but Warmane doesn't display it" : undefined}
                                aria-label={displayVals['25NM'] === '>=1' ? "Killed at least once" : undefined}
                              >{displayVals['25NM'] || '- -'}</div>
                              <div
                                className="raid-cell"
                                title={/halion/i.test(boss) && displayVals['25HC'] === '>=1' ? "Killed at least once, but Warmane doesn't display it" : undefined}
                                aria-label={displayVals['25HC'] === '>=1' ? "Killed at least once" : undefined}
                              >{displayVals['25HC'] || '- -'}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    
                  </React.Fragment>
                ))}
                
              </div>
            )}
          </div>
        </div>
        
        {/* Right Panel - Detailed Information */}
        <div className="character-right-panel">
          {/* Specialization */}
          <div className="info-section">
            <h3 className="section-title">Specialization</h3>
            {character.talents && character.talents.map((talent, idx) => (
              <a
                key={idx}
                className="talent-item"
                href={`https://armory.warmane.com/character/${encodeURIComponent(character.name)}/${encodeURIComponent(character.realm)}/talents`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={getSpecIcon(character.class, talent.tree)} alt={talent.tree} className="talent-icon" />
                <span className="talent-name">{talent.tree}</span>
                <span className="talent-points">{talent.points && talent.points.join(" / ")}</span>
              </a>
            ))}
          </div>

          {/* Player vs Player */}
          <div className="info-section">
            <h3 className="section-title">Player vs Player</h3>
            <div className="pvp-item">
              <div className="pvp-icon">
                <img src={require('../assets/kills.jpg')} alt="Kills" style={{width:24,height:24,objectFit:'cover',borderRadius:3}} />
              </div>
              <span className="pvp-label">Total Kills</span>
              <span className="pvp-value">{character.honorablekills || 0}</span>
            </div>

            {/* Arena Teams */}
            {character.pvpteams && character.pvpteams.length > 0 && [2,3,5].map(bracket => {
              const team = character.pvpteams.find(t => t.type === `${bracket}v${bracket}` || t.type === `${bracket}v${bracket} Team`);
              if (!team) return null;
              return (
                <div key={bracket} className="pvp-item">
                  <img src={(() => {
                    try {
                      return require(`../assets/${bracket}v${bracket}.jpg`);
                    } catch (e) {
                      return require('../assets/2v2.jpg'); // fallback
                    }
                  })()} alt={`${bracket}v${bracket}`} className="pvp-icon pvp-arena-icon" />
                  <span className="pvp-label">[{team.type}] {team.name}</span>
                  <span className="pvp-value">{team.rating || 0}</span>
                </div>
              );
            })}
          </div>

          {/* Professions */}
          <div className="info-section">
            <h3 className="section-title">Professions</h3>
            {character.professions && character.professions.map((prof, idx) => (
              <div key={idx} className="profession-item">
                <img src={getProfessionIcon(prof.name)} alt={prof.name} className="profession-icon" />
                <span className="profession-name">{prof.name}</span>
                <span className="profession-skill">{prof.skill} / {prof.cap || 450}</span>
              </div>
            ))}
            {Array.isArray(character.secondarySkills) && character.secondarySkills.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {character.secondarySkills.map((s, i) => (
                  <div key={`sec-${i}`} className="profession-item">
                    <img src={getProfessionIcon(s.name)} alt={s.name} className="profession-icon" />
                    <span className="profession-name">{s.name}</span>
                    <span className="profession-skill">{s.skill} / {s.cap || 450}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="info-section">
            <h3 className="section-title">Recent Activity</h3>
            <div className="activity-list">
              {Array.isArray(character.recentActivity) && character.recentActivity.length > 0 && (
                character.recentActivity.slice(0, 10).map((act, idx)=> (
                  <div key={idx} className="activity-item" onMouseEnter={(e)=>handleActivityEnter(e, act)} onMouseMove={handleActivityMove} onMouseLeave={handleActivityLeave} style={{cursor:'pointer'}} onClick={()=> act.cotHref && window.open(act.cotHref, '_blank','noopener,noreferrer')}>
                    <div className="activity-icon" style={{width:24,height:24,display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {act.iconUrl ? (
                        <img src={act.iconUrl} alt={act.title || act.type} width={24} height={24} style={{borderRadius:3,objectFit:'cover'}} />
                      ) : (
                        <span>★</span>
                      )}
                    </div>
                    <span className="activity-text" title={act.title || ''}>{act.title || 'Activity'}</span>
                    <span className="activity-time">{act.timeAgo || ''}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CharacterPanel;

// Helper to find equipment item by slotType without altering layout
function findEquipment(character, slotType){
  if(!character || !Array.isArray(character.equipment)) return null;
  return character.equipment.find(eq => eq.slotType === slotType);
}

